<template>

    <div
        ref="collapsiebleElement"
        class="collapsible"
        :class="{'open': isCurrentlyOpen, 'opening': isOpening, 'closing': isClosing}"
        @focusout="handleFocusOut"
    >
        <!-- Focus trap -->
        <div
            class="focus-trap"
            tabindex="0"
            @focus="handleFocus"
        ></div>

        <div class="header" @click="toggleCollapse">
            <img class="arrow" src="@/assets/pictos/angle-down.svg" alt="" />
            <span class="title">{{ title }}</span>
            <span class="angle"></span>
        </div>

        <div class="body" ref="collapsibleBody">
            <div class="body-content" ref="collapsibleBodyContent">
                <slot />
            </div>
        </div>
    </div>

</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';

// Time for the opening/closing transition. Must be the same as in the SCSS styles.
const TRANSITION_TIME = 300;

export default defineComponent({
    props: {
        title: {
            type: String,
            required: true,
        },
        isOpen: {
            type: Boolean,
            default: false,
        }
    },

    emits: [
        "update:isOpen",
    ],

    setup(props, {emit})
    {
        const isCurrentlyOpen = ref(props.isOpen);
        const isHardOpen = ref(props.isOpen); // "HardOpen" means triggered by user, should not be closed on focusOut
        const isOpening = ref(false);
        const isClosing = ref(false);
        const collapsibleBody = ref<InstanceType<typeof HTMLElement>>();
        const collapsibleBodyContent = ref<InstanceType<typeof HTMLElement>>();
        const collapsiebleElement = ref<InstanceType<typeof HTMLElement>>();

        function setBodyContainerHeight(): void
        {
            if (collapsibleBody.value) {
                const targetHeight = collapsibleBodyContent.value?.getBoundingClientRect().height;
                collapsibleBody.value.style.height = `${targetHeight}px`;
            }
        }

        function clearBodyContainerHeight(): void
        {
            if (collapsibleBody.value) {
                collapsibleBody.value.style.height = "";
            }
        }

        function open(): void
        {
            // If the collapsible is already transitioning, do nothing
            if (isOpening.value || isClosing.value || isCurrentlyOpen.value) {
                return;
            }

            // When opening the collapsible, we need to first set the "opening" class to setup the transition
            isCurrentlyOpen.value = true;
            isOpening.value = true;
            
            // Then we can force the "body container" height to the target height of the content.
            // Because the transition was setup, the browser will animate it smoothly.
            setBodyContainerHeight();

            // Once the transition is finished, remove the "opening" class and the fixed height
            setTimeout(() => {
                isOpening.value = false;
                clearBodyContainerHeight();

                // Report that the state has changed
                emit("update:isOpen", isCurrentlyOpen.value);
            }, TRANSITION_TIME);

        }

        function close(): void
        {
            // If the collapsible is already transitioning, do nothing
            if (isOpening.value || isClosing.value || !isCurrentlyOpen.value) {
                return;
            }

            // When closing the collapsible, we need to first force the "body container" height, so the browser
            // can know from what value to transition from.
            setBodyContainerHeight();

            // Then we can set the "closing" class which will setup the transition.
            // We need to wait for the change of setBodyContainerHeight() to be applied before setting the class,
            // this can be done by deffering the execution of 0ms.
            setTimeout(() => {
                isCurrentlyOpen.value = false;
                isClosing.value = true;
            }, 0);

            // Once the transition is finished, remove the "closing" class and the fixed height
            setTimeout(() => {
                clearBodyContainerHeight();
                isClosing.value = false;

                // Report that the state has changed
                emit("update:isOpen", isCurrentlyOpen.value);
            }, TRANSITION_TIME);
        }

        function toggleCollapse(): void
        {
            if (isCurrentlyOpen.value) {
                close();
                isHardOpen.value = false;
            }
            else {
                open();
                isHardOpen.value = true;
            }
        }

        function handleFocus(): void
        {
            //await Async.timeout(0); // Ensure that if the user clicks, the click is handled before the focus event
            open();
        }

        function handleFocusOut(e: FocusEvent): void
        {
            // Never close a hard-open tab
            if (isHardOpen.value) {
                return;
            }

            // e.relatedTarget being null means that an other element of the browser was focused, meaning
            // focus is not within the collapsible anymore
            if (!e.relatedTarget || !(e.relatedTarget instanceof Element)) {
                return close();
            }

            // Closes the collapsible when the focus leaves
            if (!collapsiebleElement.value?.contains(e.relatedTarget)) {
                close();
            }
        }

        // Toggle the collapsible if the source property is modified
        watch(
            () => props.isOpen,
            (newValue) => {
                if (newValue) {
                    open();
                }
                else {
                    close();
                }
            }
        );

        return {
            collapsibleBody,
            collapsibleBodyContent,
            collapsiebleElement,
            isCurrentlyOpen,
            isOpening,
            isClosing,
            toggleCollapse,
            handleFocus,
            handleFocusOut,
        };
    }
});

</script>


<style lang="scss" scoped>

.collapsible {
    // Time for the opening/closing transition. Must be the same as in the JS.
    $transition-time: 300ms;

    //
    // Container styles
    //

    margin-bottom: 0.75rem;

    & > .focus-trap {
        outline: none;
    }
    & > .focus-trap:focus + .header {
        outline: 1px dotted $body-font-color;
    }

    & > .header {
        position: relative;
        display: flex;
        align-items: center;
        padding: 0.5rem;
        border-bottom: 2px solid $gray-500;
        background-color: $white;
        text-transform: uppercase;
        cursor: pointer;

        & > .arrow {
            margin-right: 1.5rem;
        }

        // Angle pointing downward when the header is open. It is made of 2 superposed triangles (a gray one and
        // a white one slightly higher above).
        & > .angle {
            position: absolute;
            top: 100%;
            left: 1.25rem;

            &::before, &::after {
                content: "";
                display: block;
                border: 0.75rem solid transparent;
            }

            &::before {
                border-top-color: $gray-500;
            }

            &::after {
                position: absolute;
                top: -3px;
                border-top-color: $white;
            }
        }
    }

    & > .body > .body-content {
        padding: 1.25rem 1rem 1rem 1rem;
        border-bottom: 3px solid $gray-100;
    }

    //
    // Body animation
    //

    // Closed style
    & > .body {
        height: 0;
        overflow: hidden;
        visibility: hidden;
        transition: height $transition-time;
    }

    &.open > .body {
        visibility: visible;
        height: auto;
    }

    &.opening > .body {
        visibility: visible;
    }
    &.closing > .body {
        height: 0 !important;
        visibility: visible;
    }

    //
    // Arrow animation
    //

    // Closed style
    & > .header > .arrow {
        transform: rotateX(0deg);
        transition: transform $transition-time;
    }

    &.open > .header > .arrow {
        transform: rotateX(180deg);
    }

    //
    // Angle animation
    //

    // Closed style
    & > .header > .angle {
        transform-origin: top;
        transform: rotateX(90deg);
        transition: transform $transition-time;
    }

    &.open > .header > .angle {
        transform: rotateX(0deg);
    }

}

</style>
